import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { AdminUserRow, MaterialRow, SupplierRow, WorkorderRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, parseApiTime } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { hq } from '../session';
import { ellipsisCell, sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '待受理', color: 'orange' },
  ASSIGNED: { text: '已指派', color: 'cyan' },
  PROCESSING: { text: '处理中', color: 'processing' },
  WAIT_PART: { text: '待配件', color: 'gold' },
  FINISHED: { text: '已处理', color: 'green' },
  VERIFIED: { text: '已验收', color: 'success' },
  CLOSED: { text: '已关闭', color: 'default' },
  CANCELED: { text: '已取消', color: 'default' }
};

const ORDER_TYPE_META: Record<string, { text: string; color: string }> = {
  REPAIR: { text: '维修', color: 'blue' },
  INSTALL: { text: '安装调试', color: 'geekblue' },
  COMPLAINT: { text: '投诉', color: 'red' },
  CONSULT: { text: '咨询', color: 'default' }
};

const URGENT_META: Record<string, { text: string; color: string }> = {
  NORMAL: { text: '普通', color: 'default' },
  URGENT: { text: '紧急', color: 'orange' },
  EMERGENCY: { text: '应急', color: 'red' }
};

/** 各状态下允许的流转动作（与后端状态机一致）。 */
const NEXT_ACTIONS: Record<string, { action: WorkorderActionValue; label: string; danger?: boolean }[]> = {
  NEW: [
    { action: 'PROCESSING', label: '开始处理' },
    { action: 'CANCELED', label: '取消', danger: true }
  ],
  ASSIGNED: [
    { action: 'PROCESSING', label: '开始处理' },
    { action: 'CANCELED', label: '取消', danger: true }
  ],
  PROCESSING: [
    { action: 'WAIT_PART', label: '待配件' },
    { action: 'FINISHED', label: '完成' },
    { action: 'CANCELED', label: '取消', danger: true }
  ],
  // 后端 ServiceWorkorder 状态机：WAIT_PART → PROCESSING / FINISHED / CANCELED
  WAIT_PART: [
    { action: 'PROCESSING', label: '继续处理' },
    { action: 'FINISHED', label: '完成' },
    { action: 'CANCELED', label: '取消', danger: true }
  ],
  FINISHED: [
    { action: 'VERIFIED', label: '验收通过' },
    { action: 'PROCESSING', label: '退回处理' }
  ],
  VERIFIED: [{ action: 'CLOSED', label: '关闭' }],
  CLOSED: [],
  CANCELED: []
};

type WorkorderActionValue = 'PROCESSING' | 'WAIT_PART' | 'FINISHED' | 'VERIFIED' | 'CLOSED' | 'CANCELED';

interface CreateForm {
  orderType: 'REPAIR' | 'INSTALL' | 'COMPLAINT' | 'CONSULT';
  urgentLevel: 'NORMAL' | 'URGENT' | 'EMERGENCY';
  materialId?: string;
  supplierId?: string;
  deptName?: string;
  faultDesc: string;
  expectFinishAt?: dayjs.Dayjs;
}

/**
 * 售后工单（`WX` 单）：客服受理 → 指派 → 处理 → 完成（填方案）→ 验收 → 关闭。
 * 紧急度决定 SLA（应急 2h / 紧急 8h / 普通 24h），超时在列表与详情标红（招标"售后响应 24 小时内"）。
 */
export default function ServiceWorkorder() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<string>('ALL');
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [finishOpen, setFinishOpen] = useState(false);
  const [materialKeyword, setMaterialKeyword] = useState('');
  const [assignee, setAssignee] = useState<string | undefined>();
  const [form] = Form.useForm<CreateForm>();
  const [finishForm] = Form.useForm<{ solution: string; feeAmount?: number; partsUsedJson?: string }>();

  const list = useInfiniteQuery({
    queryKey: ['ops', 'workorder', 'list', filter],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.workorder.list({
        lastId: pageParam ?? undefined,
        size: PAGE_SIZE,
        status: filter === 'ALL' ? undefined : filter
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const detail = useQuery({
    queryKey: ['ops', 'workorder', 'detail', currentId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.workorder.detail(currentId ?? ''),
    enabled: Boolean(currentId)
  });

  // SLA 概览：以全量最近 200 单为样本，展示超时未结与响应时效（投诉 24h 闭环的验收口径）
  const overview = useQuery({
    queryKey: ['ops', 'workorder', 'overview'],
    queryFn: () => hq.workorder.list({ size: 200 }),
    staleTime: 60 * 1000
  });

  const slaSummary = useMemo(() => {
    const all = overview.data?.items ?? [];
    const closed = new Set(['VERIFIED', 'CLOSED', 'CANCELED']);
    const open = all.filter((row) => !closed.has(row.status));
    const overtime = open.filter((row) => row.isOvertime);
    const finished = all.filter((row) => row.finishAt);
    const avgHours =
      finished.length > 0
        ? finished.reduce((sum, row) => {
            const start = parseApiTime(row.createAt);
            const end = parseApiTime(row.finishAt ?? undefined);
            return sum + (start && end ? dayjs(end).diff(dayjs(start), 'minute') / 60 : 0);
          }, 0) / finished.length
        : 0;
    return { total: all.length, open: open.length, overtime: overtime.length, avgHours };
  }, [overview.data]);

  const materials = useQuery({
    queryKey: ['ops', 'workorder', 'materials', materialKeyword],
    placeholderData: keepPreviousData,
    queryFn: () => hq.material.list({ keyword: materialKeyword || undefined, size: 50 }),
    enabled: createOpen
  });

  const suppliers = useQuery({
    queryKey: ['ops', 'workorder', 'suppliers'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.catalog.suppliers({ size: 50 }),
    enabled: createOpen
  });

  // 报修科室走机构主数据下拉（工单只存 deptName 快照）
  const depts = useQuery({
    queryKey: ['ops', 'workorder', 'depts'],
    queryFn: () => hq.orgStructure.dept.list({}),
    staleTime: 10 * 60 * 1000,
    enabled: createOpen
  });

  const staff = useQuery({
    queryKey: ['ops', 'workorder', 'staff'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.admin.users.list({ size: 50 }),
    enabled: assignOpen,
    retry: false
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['ops', 'workorder'] });
  };

  const create = useMutation({
    mutationFn: (values: CreateForm) => {
      const material = materials.data?.items.find((item) => item.id === values.materialId);
      const supplier = suppliers.data?.items.find((item) => item.id === values.supplierId);
      return hq.workorder.create({
        deptName: values.deptName,
        orderType: values.orderType,
        urgentLevel: values.urgentLevel,
        materialId: values.materialId,
        materialName: material?.materialName ?? undefined,
        supplierId: values.supplierId,
        supplierName: supplier?.supplierName ?? undefined,
        faultDesc: values.faultDesc,
        expectFinishAt: values.expectFinishAt?.toISOString()
      });
    },
    onSuccess: async (result) => {
      message.success(`工单已创建 ${result.bizNo}（SLA ${result.slaHours}h）`);
      setCreateOpen(false);
      form.resetFields();
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '建单失败')
  });

  const assign = useMutation({
    mutationFn: () =>
      hq.workorder.assign(currentId ?? '', {
        assignUserId: assignee ?? '',
        assignName: staff.data?.items.find((item) => item.id === assignee)?.realName ?? undefined
      }),
    onSuccess: async () => {
      message.success('已指派');
      setAssignOpen(false);
      await invalidate();
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '指派失败')
  });

  const handle = useMutation({
    mutationFn: (payload: { action: WorkorderActionValue; solution?: string; feeAmount?: number; partsUsedJson?: string }) =>
      hq.workorder.handle(currentId ?? '', payload),
    onSuccess: async (result) => {
      message.success(result.isOvertime ? `已${STATUS_META[result.status]?.text ?? result.status}（本次超出 SLA）` : `已${STATUS_META[result.status]?.text ?? result.status}`);
      setFinishOpen(false);
      finishForm.resetFields();
      await invalidate();
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '操作失败')
  });

  const runAction = (action: WorkorderActionValue) => {
    if (action === 'FINISHED') {
      setFinishOpen(true);
      return;
    }
    if (action === 'CANCELED') {
      modal.confirm({
        title: '取消工单',
        content: '取消后不可恢复，确定继续？',
        okButtonProps: { danger: true },
        onOk: () => handle.mutateAsync({ action })
      });
      return;
    }
    handle.mutate({ action });
  };

  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];
  const order = detail.data;

  const columns: ColumnsType<WorkorderRow> = [
    {
      title: '工单号',
      dataIndex: 'bizNo',
      width: 200,
      render: (value: string, row) => <Typography.Link onClick={() => setCurrentId(row.id)}>{value}</Typography.Link>
    },
    {
      title: '类型',
      dataIndex: 'orderType',
      width: 100,
      render: (value: string) => {
        const meta = ORDER_TYPE_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '紧急度',
      dataIndex: 'urgentLevel',
      width: 90,
      render: (value: string) => {
        const meta = URGENT_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '科室', dataIndex: 'deptName', width: 140, render: (value: string | null) => value || '-' },
    { title: '物资', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '诉求', dataIndex: 'faultDesc', ellipsis: { showTitle: false }, render: ellipsisCell('faultDesc') },
    {
      title: '状态',
      dataIndex: 'status',
      width: 110,
      render: (value: string, row) => (
        <Space size={4}>
          <Tag color={STATUS_META[value]?.color ?? 'default'}>{STATUS_META[value]?.text ?? value}</Tag>
          {row.isOvertime ? <Tag color="red">超时</Tag> : null}
        </Space>
      )
    },
    { title: '期望完成', dataIndex: 'expectFinishAt', width: 150, render: (value: string | null) => formatDisplayTime(value) },
    { title: '费用', dataIndex: 'feeAmount', width: 110, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Space size={0}>
          <Button type="link" onClick={() => setCurrentId(row.id)}>
            详情
          </Button>
          {['CLOSED', 'CANCELED'].includes(row.status) ? null : (
            <Button
              type="link"
              onClick={() => {
                setCurrentId(row.id);
                setAssignee(undefined);
                setAssignOpen(true);
              }}
            >
              指派
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title="售后工单"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} loading={list.isFetching} onClick={() => void list.refetch()}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setCreateOpen(true);
              form.setFieldsValue({ orderType: 'REPAIR', urgentLevel: 'NORMAL' });
            }}
          >
            新建工单
          </Button>
        </Space>
      }
    >
      <Typography.Paragraph type="secondary">
        紧急度决定 SLA：应急 2 小时 / 紧急 8 小时 / 普通 24 小时；完成工单必须填写处理方案，超时在列表标红（招标"售后响应时间在 24 小时内"）。
      </Typography.Paragraph>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="未闭环工单" value={slaSummary.open} suffix={`/ ${slaSummary.total}`} loading={overview.isPending} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="超时未结"
              value={slaSummary.overtime}
              suffix="单"
              loading={overview.isPending}
              valueStyle={{ color: slaSummary.overtime > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="超时率"
              value={slaSummary.open > 0 ? ((slaSummary.overtime / slaSummary.open) * 100).toFixed(1) : '0.0'}
              suffix="%"
              loading={overview.isPending}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="平均处理时长" value={slaSummary.avgHours.toFixed(1)} suffix="小时" loading={overview.isPending} />
          </Card>
        </Col>
      </Row>

      <Segmented<string>
        value={filter}
        options={[
          { label: '全部', value: 'ALL' },
          { label: '待受理', value: 'NEW' },
          { label: '已指派', value: 'ASSIGNED' },
          { label: '处理中', value: 'PROCESSING' },
          { label: '待配件', value: 'WAIT_PART' },
          { label: '已处理', value: 'FINISHED' },
          { label: '已验收', value: 'VERIFIED' },
          { label: '已关闭', value: 'CLOSED' },
          { label: '已取消', value: 'CANCELED' }
        ]}
        onChange={(value) => setFilter(value)}
        style={{ marginBottom: 12 }}
      />

      {list.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="工单加载失败"
          description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
        />
      ) : null}

      <Table<WorkorderRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={list.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        locale={{ emptyText: '暂无售后工单' }}
        onRow={(row) => ({ onDoubleClick: () => setCurrentId(row.id) })}
      />

      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {list.hasNextPage ? (
          <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
            加载更多
          </Button>
        ) : null}
      </Space>

      <Drawer
        title={order?.bizNo ?? '工单详情'}
        open={currentId !== null && !assignOpen}
        onClose={() => setCurrentId(null)}
        width={860}
        extra={
          order ? (
            <Space>
              <Button
                onClick={() => {
                  setAssignee(undefined);
                  setAssignOpen(true);
                }}
              >
                指派
              </Button>
              {(NEXT_ACTIONS[order.status] ?? []).map((item) => (
                <Button
                  key={item.action}
                  type={item.action === 'FINISHED' ? 'primary' : 'default'}
                  danger={item.danger}
                  loading={handle.isPending}
                  onClick={() => runAction(item.action)}
                >
                  {item.label}
                </Button>
              ))}
            </Space>
          ) : null
        }
      >
        {detail.isLoading ? (
          <Spin />
        ) : detail.isError ? (
          <Alert
            type="error"
            showIcon
            message="详情加载失败"
            description={detail.error instanceof ApiError ? detail.error.friendlyMessage : String(detail.error)}
          />
        ) : order ? (
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="状态">
              <Space size={4}>
                <Tag color={STATUS_META[order.status]?.color ?? 'default'}>
                  {STATUS_META[order.status]?.text ?? order.status}
                </Tag>
                {order.isOvertime ? <Tag color="red">超时</Tag> : null}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="类型">{ORDER_TYPE_META[order.orderType]?.text ?? order.orderType}</Descriptions.Item>
            <Descriptions.Item label="紧急度">{URGENT_META[order.urgentLevel]?.text ?? order.urgentLevel}</Descriptions.Item>
            <Descriptions.Item label="SLA">{order.slaHours} 小时</Descriptions.Item>
            <Descriptions.Item label="机构">{order.orgId}</Descriptions.Item>
            <Descriptions.Item label="科室">{order.deptName || '-'}</Descriptions.Item>
            <Descriptions.Item label="物资">{order.materialName || '-'}</Descriptions.Item>
            <Descriptions.Item label="供应商">{order.supplierName || order.supplierId}</Descriptions.Item>
            <Descriptions.Item label="受理客服">{order.assignName || '-'}</Descriptions.Item>
            <Descriptions.Item label="期望完成">{formatDisplayTime(order.expectFinishAt)}</Descriptions.Item>
            <Descriptions.Item label="完成时间">{formatDisplayTime(order.finishAt)}</Descriptions.Item>
            <Descriptions.Item label="费用">{formatAmount(order.feeAmount)}</Descriptions.Item>
            <Descriptions.Item label="诉求" span={2}>
              {order.faultDesc || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="处理方案" span={2}>
              {order.solution || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="配件使用" span={2}>
              {order.partsUsedJson || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间" span={2}>
              {formatDisplayTime(order.createAt)}
            </Descriptions.Item>
          </Descriptions>
        ) : null}
      </Drawer>

      <Modal
        title="新建售后工单"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        destroyOnHidden
        width={700}
        confirmLoading={create.isPending}
        onOk={async () => {
          const values = await form.validateFields();
          create.mutate(values);
        }}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="orderType" label="工单类型" rules={[{ required: true }]}>
              <Select
                style={{ width: 160 }}
                options={[
                  { value: 'REPAIR', label: '维修' },
                  { value: 'INSTALL', label: '安装调试' },
                  { value: 'COMPLAINT', label: '投诉' },
                  { value: 'CONSULT', label: '咨询' }
                ]}
              />
            </Form.Item>
            <Form.Item name="urgentLevel" label="紧急度" rules={[{ required: true }]}>
              <Select
                style={{ width: 140 }}
                options={[
                  { value: 'NORMAL', label: '普通（24h）' },
                  { value: 'URGENT', label: '紧急（8h）' },
                  { value: 'EMERGENCY', label: '应急（2h）' }
                ]}
              />
            </Form.Item>
            <Form.Item name="expectFinishAt" label="期望完成时间">
              <DatePicker showTime style={{ width: 200 }} />
            </Form.Item>
          </Space>
          <Form.Item name="materialId" label="相关物资">
            <Select
              allowClear
              showSearch
              filterOption={false}
              placeholder="按名称/编码搜索（可不选）"
              onSearch={(value) => setMaterialKeyword(value)}
              options={(materials.data?.items ?? []).map((item: MaterialRow) => ({
                value: item.id,
                label: `${item.materialCode ?? ''} ${item.materialName ?? ''} ${item.spec ?? ''}`
              }))}
            />
          </Form.Item>
          <Form.Item name="supplierId" label="责任供应商">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="选择供应商（可不选）"
              options={(suppliers.data?.items ?? []).map((item: SupplierRow) => ({
                value: item.id,
                label: `${item.supplierCode} ${item.supplierName}`
              }))}
            />
          </Form.Item>
          <Form.Item name="deptName" label="报修科室">
            {/* 后端只收 deptName 快照：改成主数据下拉，避免手打错字导致工单挂不到科室 */}
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="搜索并选择科室"
              loading={depts.isPending}
              options={(depts.data ?? []).map((row) => ({ value: row.deptName, label: row.deptName }))}
            />
          </Form.Item>
          <Form.Item
            name="faultDesc"
            label="故障/诉求描述"
            rules={[
              { required: true, message: '请描述故障或诉求' },
              { min: 5, message: '不少于 5 个字' }
            ]}
          >
            <Input.TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="指派受理客服"
        open={assignOpen}
        onCancel={() => setAssignOpen(false)}
        confirmLoading={assign.isPending}
        onOk={() => assign.mutate()}
        okButtonProps={{ disabled: !assignee }}
      >
        {staff.isError ? (
          <Alert
            type="warning"
            showIcon
            message="无法加载用户列表（需要 sys:user:manage 权限）"
            description={staff.error instanceof ApiError ? staff.error.friendlyMessage : String(staff.error)}
          />
        ) : (
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            placeholder="选择客服"
            value={assignee}
            onChange={(value) => setAssignee(value)}
            options={(staff.data?.items ?? []).map((item: AdminUserRow) => ({
              value: item.id,
              label: `${item.realName ?? item.userCode}（${item.userCode}）`
            }))}
          />
        )}
      </Modal>

      <Modal
        title="完成工单"
        open={finishOpen}
        onCancel={() => setFinishOpen(false)}
        confirmLoading={handle.isPending}
        onOk={async () => {
          const values = await finishForm.validateFields();
          handle.mutate({ action: 'FINISHED', ...values });
        }}
      >
        <Form form={finishForm} layout="vertical" preserve={false}>
          <Form.Item
            name="solution"
            label="处理方案"
            rules={[
              { required: true, message: '请填写处理方案' },
              { min: 5, message: '不少于 5 个字' }
            ]}
          >
            <Input.TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>
          <Form.Item name="feeAmount" label="费用金额（免费配件填 0）">
            <InputNumber min={0} precision={2} style={{ width: 200 }} addonBefore="¥" />
          </Form.Item>
          <Form.Item name="partsUsedJson" label="配件清单（JSON，可选）">
            <Input.TextArea rows={2} placeholder='[{"name":"腕带卡扣","qty":2,"free":true}]' />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
